// ==========================================================
// cuotaPagos — cupo diario de consultas al MEF (Seguimiento de Pagos)
// ==========================================================
// La sección está abierta a todos los planes, pero cada consulta sale por el
// puente que corre en un solo equipo (`servidor-mef`), así que el plan decide
// cuántas se pueden hacer al día:
//
//   ADMI       sin límite
//   PLUS_PLUS  5 al día   (Plan Premium)
//   PREMIUM    3 al día   (Plan Plus)
//   el resto   1 al día
//
// El contador vive en Supabase y no en `localStorage`, porque es una condición
// del plan: en el navegador se borraría con el historial y volvería a cero en
// cada equipo. Quien cuenta y decide es la base
// (`supabase/migrations/202610200001_cuota_pagos_mef.sql`); acá solo se la
// pregunta. El resto de esta pantalla (historial y última consulta) sigue
// guardándose en el equipo, ver `mefExpediente.js`.
//
// El cupo se reserva ANTES de salir al MEF y se devuelve con `anularConsulta`
// si la consulta no llegó a buen puerto: un código de imagen mal digitado no
// le puede costar al usuario su consulta del día.

import { supabase } from './supabaseClient';

/** Cómo va el cupo de hoy: `null` si no se pudo preguntar. */
export const leerCuota = async (correo) => {
  if (!correo) return null;
  try {
    const { data, error } = await supabase.rpc('cuota_consultas_mef', { p_correo: correo });
    if (error) throw error;
    return data || null;
  } catch (e) {
    console.warn('[pagos] no se pudo leer el cupo del día:', e?.message);
    return null;
  }
};

/**
 * Reserva una consulta. Devuelve lo que respondió la base:
 *   { permitido: true,  id, limite, ilimitado, usadas, restantes }
 *   { permitido: false, motivo: 'cuota' | 'sesion', limite, usadas, ... }
 *
 * Si la base no contesta (sin internet, función sin desplegar) devuelve
 * `{ permitido: true, id: null, sinContador: true }`: el cupo es una regla
 * comercial y dejar la pantalla inservible por un fallo de red castigaría al
 * cliente por algo que no es suyo.
 */
export const reservarConsulta = async (correo, { anoEje, secEjec, expediente } = {}) => {
  if (!correo) return { permitido: true, id: null, sinContador: true };
  try {
    const { data, error } = await supabase.rpc('registrar_consulta_mef', {
      p_correo: correo,
      p_ano_eje: anoEje || null,
      p_sec_ejec: secEjec || null,
      p_expediente: expediente || null,
    });
    if (error) throw error;
    if (!data) return { permitido: true, id: null, sinContador: true };
    return data;
  } catch (e) {
    console.warn('[pagos] no se pudo reservar la consulta:', e?.message);
    return { permitido: true, id: null, sinContador: true };
  }
};

/** Devuelve el cupo de una consulta que no llegó a dar resultado. */
export const anularConsulta = async (correo, id) => {
  if (!correo || !id) return false;
  try {
    const { data, error } = await supabase.rpc('anular_consulta_mef', {
      p_correo: correo,
      p_id: id,
    });
    if (error) throw error;
    return Boolean(data);
  } catch (e) {
    console.warn('[pagos] no se pudo devolver el cupo:', e?.message);
    return false;
  }
};
